import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Remaining time from now -> deadline (negative = overdue). Ticks every second.
// Each consumer pays for their own 1-second re-render budget, but a real-time
// countdown without polling is the point — accept the cost.
export const useTickingRemaining = (deadline) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!deadline) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadline]);
  if (!deadline) return null;
  const t = new Date(deadline).getTime();
  if (Number.isNaN(t)) return null;
  return t - now;
};

const fmtTimeOnly = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Live HH:MM:SS countdown shown next to the priority badge.
// • Hides when status is completed/cancelled — nothing to wait for.
// • Switches to "Will be ready at HH:MM" when admin set a custom ready_at AND
//   that time is still in the future (overrides the priority deadline).
// • Goes red + "−HH:MM:SS / overdue" once the deadline has passed.
//
// `compact` (default false): two visual sizes intentionally — the order list
// row uses the small inline variant so it fits in the table cell, the order
// detail drawer uses the larger boxed variant (with the "LEFT" / "OVERDUE"
// caption) so a busy admin can read it across the room. Picked by the
// product owner — see chat 2026-06-18 (variant C).
//
// `isAdmin` (default false): when true, ALL captions stay in English to
// honour the platform's Admin English Invariant. Customers see the labels
// translated via i18n (orders.countdown.*).
export const PriorityCountdown = ({ deadline, readyAt, status, compact = false, isAdmin = false }) => {
  const { t } = useTranslation('orders');
  const tt = (key, en) => (isAdmin ? en : t(key, { defaultValue: en }));
  // When ready_at is set and still in the future → it gates everything: show
  // "Will be ready at HH:MM" (no live countdown).
  // When ready_at has elapsed → fall back to normal priority deadline
  // countdown (the order should auto-complete on the next refresh; while
  // we're waiting for that to happen, don't keep advertising the old time).
  const readyAtMs = readyAt ? new Date(readyAt).getTime() : 0;
  const readyInFuture = readyAtMs && readyAtMs > Date.now();
  const target = readyInFuture ? readyAt : deadline;
  const diff = useTickingRemaining(target);
  // Terminal statuses — completed/closed/cancelled — never show a countdown.
  // `closed` is the post-refund / archived state (Actions → "Close & refund");
  // it's just as terminal as completed/cancelled, so showing an "overdue −HH:MM:SS"
  // chip there confused operators (they had nothing left to act on). Same hide
  // rule applies to the orders-list inline variant below.
  if (status === 'completed' || status === 'closed' || status === 'cancelled') return null;
  if (!target || diff === null) return null;

  // ── "Will be ready at HH:MM" — same visual size as the active countdown
  // so it doesn't shift the layout when admin sets ready_at and back.
  if (readyInFuture) {
    if (compact) {
      return (
        <span
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-50 to-cyan-100 border border-cyan-200 shadow-sm whitespace-nowrap align-middle"
          data-testid="ready-at-label"
        >
          <Clock className="w-4 h-4 text-cyan-700" />
          <span className="leading-none">
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-cyan-700/70">{tt('countdown.ready_at', 'ready at')}</span>
            <span className="block font-mono font-bold text-base text-cyan-700 tracking-tight">{fmtTimeOnly(readyAt)}</span>
          </span>
        </span>
      );
    }
    return (
      <div
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono font-semibold text-cyan-700 whitespace-nowrap"
        data-testid="ready-at-label"
      >
        <Clock className="w-3 h-3" />
        {isAdmin ? `Will be ready at ${fmtTimeOnly(readyAt)}` : t('countdown.will_be_ready_at', { time: fmtTimeOnly(readyAt), defaultValue: `Will be ready at ${fmtTimeOnly(readyAt)}` })}
      </div>
    );
  }

  const late = diff < 0;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, '0');
  const hhmmss = `${pad(h)}:${pad(m)}:${pad(s)}`;

  // ── Detail drawer variant (compact=true) — larger boxed pill with caption.
  if (compact) {
    if (late) {
      return (
        <span
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-red-50 to-red-100 border border-red-200 shadow-sm whitespace-nowrap align-middle"
          data-testid="priority-countdown"
        >
          <AlertTriangle className="w-4 h-4 text-red-700" />
          <span className="leading-none">
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-red-700/70">{tt('countdown.overdue', 'overdue')}</span>
            <span className="block font-mono font-bold text-base text-red-700 tracking-tight">−{hhmmss}</span>
          </span>
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 shadow-sm whitespace-nowrap align-middle"
        data-testid="priority-countdown"
      >
        <Clock className="w-4 h-4 text-emerald-700" />
        <span className="leading-none">
          <span className="block text-[9px] font-semibold uppercase tracking-wider text-emerald-700/70">{tt('countdown.left', 'left')}</span>
          <span className="block font-mono font-bold text-base text-emerald-700 tracking-tight">{hhmmss}</span>
        </span>
      </span>
    );
  }

  // ── Orders-table variant (compact=false) — small inline text under badge.
  return (
    <div
      className={`mt-1 inline-flex items-center gap-1 text-[11px] font-mono font-semibold whitespace-nowrap ${late ? 'text-red-600' : 'text-emerald-700'}`}
      data-testid="priority-countdown"
    >
      {late ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {late
        ? (isAdmin ? `overdue ${hhmmss} ago` : t('countdown.overdue_ago', { time: hhmmss, defaultValue: `overdue ${hhmmss} ago` }))
        : hhmmss}
    </div>
  );
};
